import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "@bw/shared/hooks";
import { isApiError, type CompanySoftwareBillingApi } from "@bw/shared/api";
import { clearCompanyMethodCommandIntent, clearCompanyMethodCommandIntentForScope, readCompanyMethodCommandIntent, writeCompanyMethodCommandIntent, sameCompanyBillingScope } from "@bw/shared/utils";
import type { CommercialSoftwareMethodDisclosureView } from "@bw/shared/types/generated";
import type { CompanyMethodChallenge, CompanyMethodCommandIntent, ModuleAgreementBillingProps } from "./companyMethodContracts";
import { ModuleOfferDocument } from "./ModuleOfferContent";
import { CompanyMethodSetupFields } from "./CompanyMethodSetupFields";
import { ModuleMethodSetupHistory } from "./ModuleMethodSetupHistory";
import {
  companyBillingDenial, useCompanyBilling, useCompanyMethod, useCompanyMethodDisclosure, useCompanyMethodSetups,
  useSetupCompanyBilling, useConfirmCompanyBilling, useGrantCompanyMethodConsent, useRevokeCompanyMethodConsent,
  useSetupCompanyMethod, useResumeCompanyMethod,
  useResumeCompanyCustomer,
} from "./useModuleBilling";

type Operation = keyof CompanySoftwareBillingApi;

export function ModuleAgreementBilling({ copy, billingFenceRevision, onBillingDenied, onBillingRevalidated }: ModuleAgreementBillingProps) {
  const { user } = useAuth();
  const scope = useMemo(() => ({ actorId: user?.uid ?? "", orgId: copy.org_id, acceptanceId: copy.acceptance_id,
    environment: copy.offer.environment }), [user?.uid, copy.org_id, copy.acceptance_id, copy.offer.environment]);
  const scopeKey = JSON.stringify(scope);
  const live = useRef({ scope, fence: billingFenceRevision, active: true }); live.current.scope = scope; live.current.fence = billingFenceRevision;
  const [acknowledgedFence, setAcknowledgedFence] = useState(0);
  const [challenge, setChallenge] = useState<CompanyMethodChallenge | null>(null);
  const [offset, setOffset] = useState(0), [commandBusy, setBusy] = useState(false), busyRef = useRef(false), providerBusyRef = useRef(false);
  const [providerBusy, setProviderBusy] = useState(false);
  const commandOwner = useRef<symbol | null>(null);
  const busy = commandBusy || providerBusy;
  const [review, setReview] = useState<CommercialSoftwareMethodDisclosureView | null>(null);
  const [intent, setIntent] = useState<CompanyMethodCommandIntent | null>(() => readCompanyMethodCommandIntent(scope));
  const [retry, setRetry] = useState<{ operation: Operation; run: () => Promise<void> } | null>(null);
  const [message, setMessage] = useState<string | null>(null), [revoking, setRevoking] = useState(false);
  const [billingDenied, setBillingDenied] = useState(false), deniedRef = useRef(false);
  const [writeDenials, setWriteDenials] = useState<ReadonlySet<Operation>>(new Set());
  const [selected, setSelected] = useState<string | null>(null), [resuming, setResuming] = useState<string | null>(null);
  const current = () => live.current.active && live.current.fence === billingFenceRevision && sameCompanyBillingScope(live.current.scope, scope);
  const captureChallenge = (value: CompanyMethodChallenge) => {
    if (!current() || deniedRef.current || billingFenceRevision !== acknowledgedFence) throw new Error("Company billing lease changed");
    setSelected(value.operation_id); setChallenge(value);
  };
  const suspendAutomaticReads = billingDenied || billingFenceRevision !== acknowledgedFence;
  const billing = useCompanyBilling(scope, billingFenceRevision, suspendAutomaticReads), method = useCompanyMethod(scope, billingFenceRevision, suspendAutomaticReads), disclosure = useCompanyMethodDisclosure(scope, billingFenceRevision);
  const setups = useCompanyMethodSetups(scope, billingFenceRevision, suspendAutomaticReads, { limit: 10, offset });
  const setupContext = useSetupCompanyBilling(scope, billingFenceRevision), confirmContext = useConfirmCompanyBilling(scope, billingFenceRevision);
  const resumeCustomer = useResumeCompanyCustomer(scope, billingFenceRevision);
  const grant = useGrantCompanyMethodConsent(scope, billingFenceRevision), revoke = useRevokeCompanyMethodConsent(scope, billingFenceRevision);
  const setup = useSetupCompanyMethod(scope, billingFenceRevision, captureChallenge), resume = useResumeCompanyMethod(scope, billingFenceRevision, captureChallenge);
  const errors = [billing.error, method.error, disclosure.error, setups.error, setupContext.error, confirmContext.error,
    grant.error, revoke.error, setup.error, resume.error, resumeCustomer.error];
  const seen = useRef(new Set<Error>());
  const rechecked = useRef(new Set<Error>());

  useLayoutEffect(() => {
    live.current.active = true;
    const restored = readCompanyMethodCommandIntent(scope);
    setIntent(restored);
    return () => {
      live.current.active = false;
      queueMicrotask(() => { if (!live.current.active) clearCompanyMethodCommandIntentForScope(scope); });
    };
  }, [scopeKey]);

  useEffect(() => {
    if (billingFenceRevision !== acknowledgedFence) {
      deniedRef.current = true; setBillingDenied(true); setChallenge(null); setReview(null); setRetry(null);
      clearCompanyMethodCommandIntentForScope(scope); setIntent(null);
    }
  }, [billingFenceRevision, acknowledgedFence]);

  useEffect(() => {
    for (const error of errors) {
      if (!error || seen.current.has(error) || !current()) continue;
      seen.current.add(error);
      const denial = companyBillingDenial(error);
      if (!denial || !sameCompanyBillingScope(denial.scope, scope)) continue;
      if (["setupBilling", "confirmBilling", "grantConsent", "setupMethod", "revokeConsent", "resumeMethod", "resumeCustomer"].includes(denial.operation))
        setWriteDenials(old => new Set([...old, denial.operation]));
      if (denial.category === "new_admission_restricted") {
        // Preserve independently authorized history, exact revoke and recovery.
      } else if (!deniedRef.current) {
        deniedRef.current = true; setBillingDenied(true); setChallenge(null); setReview(null);
        clearCompanyMethodCommandIntent(); setIntent(null);
      }
      onBillingDenied(denial);
    }
  });

  async function run(operation: Operation, action: () => Promise<unknown>, captured: CompanyMethodCommandIntent | null): Promise<void> {
    if (busyRef.current || providerBusyRef.current || !current() || deniedRef.current) return;
    if (captured && !writeCompanyMethodCommandIntent(captured)) {
      setMessage("This tab cannot safely retain the original request. No new request was sent."); return;
    }
    if (captured) setIntent(captured);
    const owner = Symbol(); commandOwner.current = owner;
    busyRef.current = true; setBusy(true); setMessage(null);
    try {
      await action();
      if (!current()) return;
      if (captured) { clearCompanyMethodCommandIntent(); setIntent(null); }
      setRetry(previous => previous?.operation === operation ? null : previous);
      setWriteDenials(old => { const next = new Set(old); next.delete(operation); return next; });
      if (operation === "grantConsent") setReview(null);
    } catch (error) {
      if (!current()) return;
      const terminal = isApiError(error) && error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status);
      if (terminal) { clearCompanyMethodCommandIntent(); setIntent(null); }
      if (terminal && ![401, 403].includes(isApiError(error) ? error.status : 0)) {
        setRetry(null); setReview(null); setMessage("The request no longer matches current facts. Refresh and review before a new request.");
      } else {
        setRetry({ operation, run: () => run(operation, action, captured) });
        setMessage(companyBillingDenial(error)?.category === "new_admission_restricted"
          ? "New company billing actions are restricted. Existing agreement access and recovery are checked separately."
          : "Request outcome is unresolved. Retry its original inputs or rejoin the original setup.");
      }
    } finally {
      if (commandOwner.current === owner) {
        commandOwner.current = null; busyRef.current = false;
        if (live.current.active && sameCompanyBillingScope(live.current.scope, scope)) setBusy(false);
      }
    }
  }

  async function executeIntent(captured: CompanyMethodCommandIntent): Promise<void> {
    if (!sameCompanyBillingScope(captured.scope, scope)) return;
    if (captured.kind === "grant") return run("grantConsent", () => grant.mutateAsync(captured.request), captured);
    if (captured.kind === "revoke") return run("revokeConsent", () => revoke.mutateAsync(captured.request), captured);
    return run("setupMethod", async () => { const value = await setup.mutateAsync(captured.request); if (current()) setSelected(value.operation_id); }, captured);
  }

  async function reviewDisclosure() {
    if (busyRef.current || providerBusyRef.current || deniedRef.current) return;
    setReview(null); setMessage(null);
    const result = await disclosure.refetch();
    if (current() && result.isSuccess) setReview(result.data);
  }

  async function recheckBilling() {
    const results = await Promise.all([billing.refetch(), method.refetch(), setups.refetch()]);
    if (current() && results.every(result => result.isSuccess)) {
      for (const error of errors) if (error) rechecked.current.add(error);
      deniedRef.current = false; setBillingDenied(false); setMessage(null);
      setAcknowledgedFence(billingFenceRevision);
      onBillingRevalidated({ scope, billingFenceRevision });
      setWriteDenials(old => new Set([...old].filter(value => !["resumeMethod", "resumeCustomer", "revokeConsent"].includes(value))));
      // Only this explicit protective-action review restores its own latches;
      // unrelated/background reads never restore financial admission.
    }
  }

  async function rejoin(operationId: string) {
    setChallenge(null); setSelected(operationId); setResuming(operationId);
    await run("resumeMethod", () => resume.mutateAsync(operationId), null);
    if (current()) setResuming(null);
  }

  const last = resume.data?.operation_id === selected ? resume.data : setup.data?.operation_id === selected ? setup.data : null;
  const currentConsent = method.data?.current_consent;
  const unresolvedRevoke = intent?.kind === "revoke" || retry?.operation === "revokeConsent";
  const originalCustomer = billing.isSuccess && !billing.data.customer_configured ? billing.data.customer_setup : null;
  const hash = review?.terms_fingerprint ?? grant.data?.terms_fingerprint;
  const hasPending = setups.data?.items.some(item => ["pending", "uncertain"].includes(item.operation_status)) || method.data?.state === "setup_pending";
  const loading = billing.isPending || method.isPending || setups.isPending;
  const immediateLoss = errors.some(error => {
    const denial = companyBillingDenial(error);
    return error && !rechecked.current.has(error) && denial && denial.category !== "new_admission_restricted" && sameCompanyBillingScope(denial.scope, scope);
  });
  if (billingDenied || immediateLoss || billingFenceRevision !== acknowledgedFence) return <Alert severity="warning">Current company billing access is unavailable.
    <Button size="small" disabled={busy} onClick={() => void recheckBilling()}>Recheck billing access</Button>
  </Alert>;
  return <Stack spacing={1.5} aria-label="Company card setup">
    {message ? <Alert severity="warning">{message}</Alert> : null}
    {writeDenials.size > 0 ? <Alert severity="warning">A new billing action was denied. Reading the agreement or checking setup status does not restore that action.</Alert> : null}
    {loading ? <Typography role="status">Loading company billing state…</Typography> : null}
    {(billing.isError || method.isError || setups.isError) ? <Alert severity="warning">Company billing information is unavailable.
      <Button size="small" onClick={() => void recheckBilling()}>Retry billing read</Button></Alert> : null}
    <Button size="small" variant="outlined" disabled={busy || disclosure.isFetching} onClick={() => void reviewDisclosure()}>
      Review company card permission
    </Button>
    {disclosure.isError ? <Alert severity="warning">Approved company card disclosure is unavailable. Existing permission revocation and setup recovery remain separate.</Alert> : null}
    {review ? <Stack spacing={1.5}>
      <ModuleOfferDocument document={review.document} />
      {(!currentConsent || method.data?.state === "revoked" || currentConsent.decision === "revoke") ?
        <Button size="small" variant="contained" disabled={busy || intent !== null || writeDenials.has("grantConsent")} onClick={() => void executeIntent({ version: 1, scope, kind: "grant", request: {
          environment: scope.environment, command_id: crypto.randomUUID(), acceptance_id: scope.acceptanceId,
          reviewed_terms_fingerprint: review.terms_fingerprint, disclosure_version_id: review.disclosure_version_id,
          reviewed_disclosure_sha256: review.document.content_sha256, expected_consent_id: review.current_consent_id,
          expected_consent_sequence: review.consent_sequence,
        } })}>Allow company card use</Button> : <Typography variant="body2">Company card permission is already recorded.</Typography>}
    </Stack> : null}
    {billing.isSuccess && billing.data.context_state === "unconfigured" ? <Button size="small" variant="outlined" disabled={busy || intent !== null || writeDenials.has("setupBilling")}
      onClick={() => { const request = { environment: scope.environment, command_id: crypto.randomUUID() };
        void run("setupBilling", () => setupContext.mutateAsync(request), null); }}>Set up company billing</Button> : null}
    {originalCustomer ? <Alert severity="warning">
      {originalCustomer.operation_status === "completed"
        ? "Original customer setup is recorded. Recovery does not re-enable the current configuration."
        : "Company customer setup is unresolved. This page will not create a replacement customer."}
      <Button size="small" disabled={busy || writeDenials.has("resumeCustomer")}
        onClick={() => void run("resumeCustomer", () => resumeCustomer.mutateAsync(originalCustomer.operation_id), null)}>
        Resume company billing setup
      </Button>
    </Alert> : null}
    {billing.data?.context_state === "revoked" ? <Alert severity="warning">Company billing configuration is revoked; it is not automatically re-enabled.</Alert> : null}
    {billing.isSuccess && billing.data.customer_configured ? <Button size="small" disabled={busy || intent !== null || writeDenials.has("confirmBilling")}
      onClick={() => { const request = { environment: scope.environment, command_id: crypto.randomUUID() };
        void run("confirmBilling", async () => { const value = await confirmContext.mutateAsync(request);
          if (current() && value.disposition === "action_required") setMessage(value.reason === "new_customer_setup_required"
            ? "The selected billing scope requires new customer setup and permission; no existing card is adopted."
            : "Original customer setup is still pending."); }, null); }}>Confirm billing configuration</Button> : null}
    {billing.isSuccess && method.isSuccess && setups.isSuccess && billing.data.customer_configured && currentConsent?.decision === "grant" && method.data.state !== "revoked" && hash && !hasPending && !challenge ?
      <Button size="small" variant="contained" disabled={busy || intent !== null || writeDenials.has("setupMethod")} onClick={() => void executeIntent({ version: 1, scope, kind: "setup", request: {
        environment: scope.environment, command_id: crypto.randomUUID(), acceptance_id: scope.acceptanceId,
        consent_id: currentConsent.consent_id, reviewed_terms_fingerprint: hash,
      } })}>Add company card</Button> : null}
    {challenge ? <CompanyMethodSetupFields challenge={challenge} onReconcile={async id => {
      if (current()) { providerBusyRef.current = false; setProviderBusy(false); await rejoin(id); }
    }} onDismiss={() => setChallenge(null)} onBusyChange={value => {
      if ((value && current()) || (!value && live.current.active && sameCompanyBillingScope(scope, live.current.scope))) {
        providerBusyRef.current = value; setProviderBusy(value);
      }
    }} /> : null}
    {last ? <Typography role="status">{last.disposition === "failed" ? "Card setup failed."
      : last.disposition === "processing" ? "Card setup is processing; check its original status."
      : last.disposition === "uncertain" ? "Card setup outcome is unknown."
      : last.disposition === "succeeded" ? (last.application === "current" ? "Card setup complete. No purchase or licence activation is implied." : "Setup recorded; this card is not current.")
      : "Continue the original card setup."}</Typography> : null}
    {setups.isSuccess ? <ModuleMethodSetupHistory page={setups.data} resumingOperationId={resuming} resumeDisabled={busy || writeDenials.has("resumeMethod")} onResume={id => void rejoin(id)} onPage={setOffset} /> : null}
    {(intent || retry) ? <Button size="small" disabled={busy} onClick={() => void (retry ? retry.run() : intent ? executeIntent(intent) : undefined)}>Retry original request</Button> : null}
    {method.isSuccess && currentConsent?.revocable ? <Button size="small" disabled={busy || unresolvedRevoke || writeDenials.has("revokeConsent")} onClick={() => setRevoking(true)}>Revoke company card permission</Button> : null}
    <Dialog open={revoking} onClose={() => !busy && setRevoking(false)} slotProps={{ paper: { role: "alertdialog", "aria-labelledby": "company-revoke-title" } }}>
      <DialogTitle id="company-revoke-title">Revoke this company card permission?</DialogTitle>
      <DialogContent>Already-dispatched work may still be recorded. This does not refund money or cancel an agreement.</DialogContent>
      <DialogActions><Button size="small" disabled={busy} onClick={() => setRevoking(false)}>Cancel</Button>
        <Button size="small" disabled={busy || unresolvedRevoke || !currentConsent?.revocable} onClick={() => {
          if (unresolvedRevoke || !currentConsent?.revocable) return;
          setRevoking(false); void executeIntent({ version: 1, scope, kind: "revoke", request: {
            environment: scope.environment, command_id: crypto.randomUUID(), consent_id: currentConsent.consent_id,
          } });
        }}>Confirm revocation</Button></DialogActions>
    </Dialog>
  </Stack>;
}
